// src/MainLayout.jsx
import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Tooltip,
  GlobalStyles,
  Paper,
  TextField,
  Button,
  Avatar,
  CircularProgress,
  Badge,
  Drawer,
  // 👇 nuevos para el modal
  Dialog, DialogTitle, DialogContent, DialogActions,
  useMediaQuery
} from '@mui/material';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';

import LogoutIcon from '@mui/icons-material/Logout';
import GroupIcon from '@mui/icons-material/Group';
import RefreshIcon from '@mui/icons-material/Refresh';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PaidIcon from '@mui/icons-material/Paid';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import WarehouseRoundedIcon from '@mui/icons-material/WarehouseRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import RuleIcon from '@mui/icons-material/Rule';
import PaymentExceptions from './PaymentExceptions';

import PagosProv from './PagosProv';
import CfdiTable from './CfdiTable';
import UploadModal from './UploadModal';
import LoginScreen from './auth/LoginScreen';
import Usuarios from './Usuarios';
import CartaPorteTable from './CartaPorteTable';
import EntradasMercancia from './EntradaMercancia';
import PedidosTable from './PedidosTable';

import axios from 'axios';
import logo from './Logo_VerdeValle.png';

// 👇 Usa tu PNG local. Si prefieres ponerlo en /public, usa const agentDefault = '/agent.png'
import agentDefault from './Verdit_neutral.png';

// Markdown renderer
import { default as ReactMarkdown } from 'react-markdown';
import { default as remarkGfm } from 'remark-gfm';

// Tipografías Roboto
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

// ===============================
// Config
// ===============================
const API_BASE = 'https://facturas.verdevalle.com.mx';
// Altura de tu AppBar (para que el Drawer no lo tape)
const APPBAR_HEIGHT = 50;
// Límites del ancho del Drawer (ajustable)
const MIN_DRAWER_W = 340;
const MAX_DRAWER_W = 720;
const DEFAULT_DRAWER_W = 420;

// Tema MUI
const theme = createTheme({
  palette: {
    primary: { main: '#119B46' },     // Verde VerdeValle
    secondary: { main: '#FFED00' },   // Amarillo encabezado
    background: { default: '#f8fafc' },
    text: { primary: '#1f2937' }
  },
  typography: {
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottom: '1px solid rgba(0,0,0,0.08)'
        }
      }
    },
    MuiToolbar: {
      styleOverrides: {
        root: { minHeight: 50 },
        dense: { minHeight: 50 }
      }
    }
  }
});

/** Heurística: ¿parece una tabla GFM? */
function looksLikeTable(md) {
  if (!md) return false;
  const lines = md.split('\n').map(l => l.trim());
  const pipeLines = lines.filter(l => /\|/.test(l));
  if (pipeLines.length < 2) return false;
  const hasSep = lines.some(l => /\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?/.test(l));
  return hasSep;
}

/** Normaliza markdown para que las tablas no queden dentro de bloques de código o por sangría */
function normalizeMarkdown(text = '') {
  let out = String(text);

  // 1) Desenrollar fences con lenguaje explícito si contienen tabla
  out = out.replace(/```(?:markdown|md|txt)\s*([\s\S]*?)```/gi, (_m, inner) => inner);

  // 2) Desenrollar cualquier fence que realmente contenga una tabla
  out = out.replace(/```+\s*([\s\S]*?)```+/g, (_m, inner) => {
    return looksLikeTable(inner) ? inner : '```\n' + inner + '\n```';
  });

  // 3) Quitar indentación de TODAS las líneas que provocan code-blocks por sangría
  out = out.replace(/^(?: {4,}|\t+)/gm, '');

  // 4) Limpieza final
  out = out.replace(/^\s+$/gm, '').trim();

  return out;
}

// ===============================
// 🔔 Modal de Bienvenida con PDF (forzado a "Leído" por día - zona MX)
// ===============================

// Configura aquí el PDF y la versión del aviso
const PDF_URL = '/AvisoVerdeValle.pdf';
const ANNOUNCE_VERSION_CONST = 'v1.0-bienvenida-2025';

// Utilidades de fecha para zona America/Mexico_City
const tz = 'America/Mexico_City';
const todayMX = () => {
  const d = new Date();
  const y = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric' }).format(d);     // 2025
  const m = new Intl.DateTimeFormat('en-CA', { timeZone: tz, month: '2-digit' }).format(d);    // 11
  const day = new Intl.DateTimeFormat('en-CA', { timeZone: tz, day: '2-digit' }).format(d);    // 10
  return `${y}-${m}-${day}`; // 2025-11-10
};

// Clave única POR DÍA + versión + usuario
const getWelcomeAckKeyDaily = (user, dateStr = todayMX()) => {
  const uid = user?.id || user?.username || user?.correo || user?.email || user?.nombre || 'anon';
  return `vv_welcome_ack_${uid}_${ANNOUNCE_VERSION_CONST}_${dateStr}`;
};

// Componente del modal (bloqueado hasta "Leído")
const WelcomePDFModal = ({ open, onAcknowledge, user, title = 'Aviso en el Portal de Proveedores' }) => {
  const muiTheme = useTheme();
  const fullScreen = useMediaQuery(muiTheme.breakpoints.down('md'));
  const storageKey = useMemo(() => getWelcomeAckKeyDaily(user), [user]);

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = PDF_URL;
    a.download = 'AvisoVerdeValle.pdf';
    a.click();
  };

  const markAsRead = () => {
    try {
      localStorage.setItem(storageKey, '1'); // marca leído PARA HOY (zona MX)
    } catch (_e) { /* ignore */ }
    onAcknowledge?.();
  };

  return (
    <Dialog
      open={open}
      // ❗ No pasamos onClose para impedir cierre por backdrop/ESC
      disableEscapeKeyDown
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 2,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PictureAsPdfRoundedIcon color="primary" />
        {title}
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Encabezado corporativo */}
        <Box sx={{ p: 2, background: 'linear-gradient(135deg,#0F8B3D,#17C963)', color: '#fff' }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Información importante para comenzar
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Revisa el documento de aviso. Al presionar <b>Leído</b>, no volverá a mostrarse hoy.
            Mañana volverá a aparecer para confirmar lectura diaria.
          </Typography>
        </Box>

        {/* Visor PDF embebido (fallback a descarga) */}
        <Box sx={{ height: fullScreen ? 'calc(100vh - 220px)' : 600, bgcolor: '#f5f5f5' }}>
          <object
            data={PDF_URL}
            type="application/pdf"
            width="100%"
            height="100%"
          >
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" gutterBottom>
                Tu navegador no pudo mostrar el PDF embebido.
              </Typography>
              <Button onClick={handleDownload} variant="outlined">
                Descargar Manual
              </Button>
            </Box>
          </object>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={handleDownload} variant="outlined">
          Descargar PDF
        </Button>
        <Button
          onClick={markAsRead}
          variant="contained"
          sx={{ bgcolor: '#119B46' }}
        >
          Leído
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ===============================
// Chat Widget (burbuja tipo "pill" + Drawer lateral)
// ===============================
const ChatWidget = ({ user }) => {
  const [open, setOpen] = useState(false);         // Drawer abierto/cerrado
  const [hovered, setHovered] = useState(false);   // Hover del botón "pill"
  const [drawerW, setDrawerW] = useState(DEFAULT_DRAWER_W); // ancho ajustable
  const [resizing, setResizing] = useState(false);

  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      role: 'assistant',
      text: `¡Hola ${user?.nombre || ''}! Soy tu asistente del Portal de Proveedores.
Puedo ayudarte a consultar **órdenes de compra**, **facturas**, **pagos** y **cartas porte**.

Ejemplos:
• "¿Qué órdenes tengo del último mes?"
• "Detalle de la OC 45000xxxx"
• "Estatus de la factura 5100xxxxx"
• "Pagos de octubre"
(Datos en tiempo real del portal)`,
      ts: Date.now()
    }
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const [minimized, setMinimized] = useState(false);
  const containerRef = useRef(null);

  // 👇 Fuente del avatar del agente: ENV opcional o PNG local (con fallback al ícono)
  const envAvatarUrl = typeof import.meta !== 'undefined' ? import.meta.env?.VITE_AGENT_AVATAR_URL : undefined;
  const [agentSrc, setAgentSrc] = useState(envAvatarUrl || agentDefault);

  const scrollToBottom = () => {
    if (!containerRef.current) return;
    containerRef.current.scrollTop = containerRef.current.scrollHeight;
  };

  useEffect(() => { scrollToBottom(); }, [messages, open, minimized]);

  useEffect(() => {
    if (!open) {
      const last = messages.filter(m => m.role === 'assistant').length;
      setUnread(last);
    } else {
      setUnread(0);
    }
  }, [open]); // eslint-disable-line

  // --- resize del Drawer (arrastra el borde izquierdo)
  useEffect(() => {
    if (!resizing) return;
    const start = { x: 0, w: drawerW };
    const onMove = (e) => {
      if (!start.x) start.x = e.clientX;
      const dx = start.x - e.clientX; // mover a la izquierda => más ancho
      const next = Math.min(MAX_DRAWER_W, Math.max(MIN_DRAWER_W, start.w + dx));
      setDrawerW(next);
    };
    const onUp = () => setResizing(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resizing, drawerW]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;

    const userMsg = {
      id: `u-${Date.now()}`,
      role: 'user',
      text,
      ts: Date.now()
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setSending(true);

    try {
      const { data } = await axios.post(
        `${API_BASE}/api/agent/chat`,
        { text },
        { withCredentials: true }
      );

      const replyText = data?.reply || 'No tuve respuesta.';
      const aiMsg = {
        id: `a-${Date.now()}`,
        role: 'assistant',
        text: replyText,
        ts: Date.now()
      };
      setMessages(prev => [...prev, aiMsg]);
    } catch (err) {
      const errMsg = {
        id: `e-${Date.now()}`,
        role: 'assistant',
        text: 'Lo siento, hubo un error al consultar el agente. Intenta nuevamente.',
        ts: Date.now()
      };
      setMessages(prev => [...prev, errMsg]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Avatares
  const UserAvatar = () => (
    <Avatar sx={{ bgcolor: '#119B46' }}>
      {String(user?.nombre || user?.username || 'U').charAt(0).toUpperCase()}
    </Avatar>
  );

  // 👇 Aquí usamos el PNG del agente (con fallback al ícono)
  const BotAvatar = () => (
    <Avatar
      src={agentSrc}
      alt="Agente VV"
      onError={() => setAgentSrc(undefined)}
      sx={{ bgcolor: agentSrc ? 'transparent' : '#111827' }}
    >
      {!agentSrc && <RocketLaunchRoundedIcon fontSize="small" sx={{ color: '#fff' }} />}
    </Avatar>
  );

  return (
    <>
      {/* Botón flotante tipo "pill" que se estira al hover (VERDE VV) */}
      <Box sx={{ position: 'fixed', bottom: 22, right: 22, zIndex: (t) => t.zIndex.modal + 1 }}>
        <Badge badgeContent={unread} color="error" overlap="circular" invisible={!unread}>
          <Box
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={() => { setOpen(true); setMinimized(false); }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              height: 58,
              width: hovered ? 210 : 58, // de círculo a pill
              transition: 'width .22s ease, box-shadow .18s ease, transform .18s ease',
              borderRadius: '29px',
              px: hovered ? 1.5 : 0,
              boxShadow: '0 12px 24px rgba(0,0,0,.18)',
              background: 'linear-gradient(135deg,#119B46 0%,#15C35A 100%)',
              color: '#fff',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ width: 58, height: 58, minWidth: 58, display: 'grid', placeItems: 'center' }}>
              {/* 👇 Avatar del agente también en la pill */}
              <Avatar
                src={agentSrc}
                alt="Agente VV"
                onError={() => setAgentSrc(undefined)}
                sx={{ width: 36, height: 36, bgcolor: agentSrc ? 'transparent' : 'rgba(0,0,0,.35)' }}
              >
                {!agentSrc && <ChatBubbleIcon sx={{ color: '#fff' }} />}
              </Avatar>
            </Box>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'nowrap',
                opacity: hovered ? 1 : 0,
                transform: `translateX(${hovered ? 0 : 8}px)`,
                transition: 'opacity .18s ease .05s, transform .18s ease .05s',
                fontWeight: 700
              }}
            >
              Asistente VV
            </Typography>
          </Box>
        </Badge>
      </Box>

      {/* Drawer lateral (NO tapa el AppBar y es AJUSTABLE) */}
      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        anchor="right"
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            top: `${APPBAR_HEIGHT}px`,
            height: `calc(100% - ${APPBAR_HEIGHT}px)`,
            width: drawerW,
            maxWidth: '100vw',
            borderTopLeftRadius: 12,
            borderBottomLeftRadius: 12,
            boxShadow: '0 20px 40px rgba(0,0,0,.25)',
            overflow: 'hidden',
            position: 'fixed'
          }
        }}
      >
        {/* Header del Drawer (verde corporativo) */}
        <Box
          sx={{
            p: 1.4,
            px: 1.8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(135deg,#0F8B3D,#17C963)',
            color: '#fff'
          }}
        >
          <Typography sx={{ fontWeight: 800 }}>Asistente VV</Typography>
          <Box>
            <IconButton size="small" sx={{ color: '#fff' }} onClick={() => setOpen(false)}>
              <CloseRoundedIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Asa para ajustar ancho (borde izquierdo) */}
        <Box
          onMouseDown={() => setResizing(true)}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: 6,
            height: '100%',
            cursor: 'ew-resize',
            zIndex: 10,
            '&:hover': { backgroundColor: 'rgba(17,155,70,0.12)' }
          }}
          title="Arrastra para ajustar el ancho"
        />

        {/* Cuerpo con tus mensajes (misma UI) */}
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {!minimized && (
            <>
              <Box
                ref={containerRef}
                sx={{
                  flex: 1,
                  p: 1.4,
                  pt: 1.2,
                  overflowY: 'auto',
                  background: 'linear-gradient(180deg,#fafafa,#f3f7f5)'
                }}
              >
                {messages.map(m => {
                  const isUser = m.role === 'user';
                  const normalized = isUser ? m.text : normalizeMarkdown(m.text);
                  return (
                    <Box
                      key={m.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        mb: 1.2,
                        flexDirection: isUser ? 'row-reverse' : 'row'
                      }}
                    >
                      {isUser ? <UserAvatar /> : <BotAvatar />}

                      <Box
                        sx={{
                          maxWidth: '75%',
                          px: 1.4,
                          py: 1,
                          borderRadius: isUser ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
                          bgcolor: isUser ? '#119B46' : '#fff',
                          color: isUser ? '#fff' : '#111827',
                          boxShadow: isUser ? '0 2px 10px rgba(17,155,70,.25)' : '0 2px 10px rgba(0,0,0,.06)',
                          border: isUser ? 'none' : '1px solid rgba(0,0,0,.06)',
                          // estilos para markdown
                          '& table': { borderCollapse: 'collapse', width: '100%', marginTop: 6, marginBottom: 6 },
                          '& th, & td': { border: '1px solid #e5e7eb', padding: '4px 6px', fontSize: '0.85rem', verticalAlign: 'top' },
                          '& th': { backgroundColor: '#f3f4f6', fontWeight: 700 },
                          '& code': {
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                            fontSize: '0.8rem',
                            backgroundColor: 'rgba(0,0,0,0.06)',
                            padding: '1px 4px',
                            borderRadius: 4
                          },
                          '& pre': { overflowX: 'auto', padding: '8px 10px', backgroundColor: 'rgba(0,0,0,0.06)', borderRadius: 6 },
                          '& a': { color: '#1976d2', textDecoration: 'none' },
                          '& a:hover': { textDecoration: 'underline' }
                        }}
                      >
                        {isUser ? (
                          <Typography variant="body2" sx={{ lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
                            {normalized}
                          </Typography>
                        ) : (
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              // Wrapper con scroll horizontal
                              table: ({node, ...props}) => (
                                <Box sx={{ width: '100%', overflowX: 'auto', my: 0.5 }}>
                                  <Box
                                    component="table"
                                    sx={{
                                      borderCollapse: 'collapse',
                                      width: '100%',
                                      tableLayout: 'fixed',
                                      minWidth: 480
                                    }}
                                    {...props}
                                  />
                                </Box>
                              ),
                              thead: ({node, ...props}) => (
                                <Box component="thead" sx={{ display: 'table', width: '100%', tableLayout: 'fixed', backgroundColor: '#f3f4f6' }} {...props} />
                              ),
                              tbody: ({node, ...props}) => (
                                <Box component="tbody" sx={{ display: 'table', width: '100%', tableLayout: 'fixed' }} {...props} />
                              ),
                              th: ({node, ...props}) => (
                                <Box
                                  component="th"
                                  sx={{
                                    border: '1px solid #e5e7eb',
                                    p: '4px 6px',
                                    fontSize: '0.85rem',
                                    fontWeight: 700,
                                    textAlign: 'left',
                                    whiteSpace: 'nowrap'
                                  }}
                                  {...props}
                                />
                              ),
                              td: ({node, ...props}) => (
                                <Box
                                  component="td"
                                  sx={{
                                    border: '1px solid #e5e7eb',
                                    p: '4px 6px',
                                    fontSize: '0.85rem',
                                    verticalAlign: 'top',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: 140
                                  }}
                                  {...props}
                                />
                              )
                            }}
                          >
                            {normalized}
                          </ReactMarkdown>
                        )}
                      </Box>
                    </Box>
                  );
                })}

                {sending && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', color: '#0F8B3D', ml: .5 }}>
                    <CircularProgress size={16} thickness={5} />
                    <Typography variant="caption">Consultando el portal…</Typography>
                  </Box>
                )}
              </Box>

              {/* Input */}
              <Box sx={{ p: 1.2, borderTop: '1px solid rgba(0,0,0,.06)', bgcolor: '#fff' }}>
                <TextField
                  placeholder='Escribe tu mensaje… (Enter para enviar)'
                  fullWidth
                  multiline
                  minRows={1}
                  maxRows={4}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={sending}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '& fieldset': { borderColor: 'rgba(0,0,0,.12)' },
                      '&:hover fieldset': { borderColor: '#0F8B3D' },
                      '&.Mui-focused fieldset': { borderColor: '#0F8B3D' }
                    },
                    mb: 1
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Consejos: “mis órdenes”, “detalle OC 450…”, “pagos de octubre”
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={sendMessage}
                    endIcon={<SendRoundedIcon />}
                    disabled={sending || !input.trim()}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '12px',
                      px: 1.8,
                      py: .7,
                      boxShadow: '0 6px 16px rgba(17,155,70,.25)',
                      bgcolor: '#119B46'
                    }}
                  >
                    Enviar
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
};

// Icono “minimizar” (por si lo necesitas después)
const RemoveIconSafe = (props) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// ===============================
// Layout principal (tu código + widget)
// ===============================
const MainLayoutContent = ({ user, setUser }) => {
  const [openUpload, setOpenUpload] = useState(false);
  const [cfdiRefreshKey, setCfdiRefreshKey] = useState(0);
  const tableRef = useRef();
  const navigate = useNavigate();
const canManagePaymentExceptions = user?.roles?.includes('formaspagos');
  const handleLogout = async () => {
    try {
      await axios.post(`${API_BASE}/api/logout`, {}, { withCredentials: true });
      setUser(null);
      navigate('/');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const handleRefreshCfdiTable = () => {
    setCfdiRefreshKey(prev => prev + 1);
    if (tableRef.current?.refreshData) {
      tableRef.current.refreshData();
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <GlobalStyles styles={{ body: { backgroundColor: '#f8fafc' } }} />

      <AppBar
        position="fixed"
        color="secondary"
        sx={{
          height: '50px',
          justifyContent: 'center',
          boxShadow: '0 8px 16px -8px rgba(0,0,0,.18)',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          zIndex: (t) => t.zIndex.drawer + 1
        }}
      >
        <Toolbar
          variant="dense"
          sx={{ minHeight: '50px !important', px: 1.5 }}
        >
          <Box sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            '& img': { height: 24 }
          }}>
            <img src={logo} alt="Logo Verde Valle" />
          </Box>

          <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500, mr: 1.5 }}>
            <b>Bienvenido/a</b> {user.nombre || user.nombre_completo}
          </Typography>

          <Box sx={{
            display: 'flex',
            '& .MuiIconButton-root': {
              p: '7px',
              borderRadius: '10px',
              transition: 'all 0.3s ease',
              '& svg': {
                fontSize: 22,
                color: 'inherit'
              },
              '&:hover': {
                background: 'linear-gradient(145deg, rgba(17,155,70,0.12), rgba(17,155,70,0.05))',
                transform: 'scale(1.08)',
                boxShadow: '0 2px 5px rgba(0, 0, 0, 0.12)',
              }
            }
          }}>
            <Tooltip title="Descargar Manual de Usuario">
              <IconButton
                component="a"
                href="/ManualProveedor.pdf"
                download
                sx={{ color: '#4b5563', '&:hover svg': { color: '#1f2937' } }}
              >
                <PictureAsPdfRoundedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Pagos a Proveedores">
              <IconButton
                onClick={() => navigate('/pagos')}
                sx={{
                  color: '#4b5563',
                  '&:hover svg': { color: '#1f2937' }
                }}
              >
                <PaidIcon />
              </IconButton>
            </Tooltip>



            <Tooltip title="Transportes">
              <IconButton
                onClick={() => navigate('/transportes')}
                sx={{
                  color: '#4b5563',
                  '&:hover svg': { color: '#1f2937' }
                }}
              >
                <LocalShippingIcon />
              </IconButton>
            </Tooltip>

      {canManagePaymentExceptions && (
              <Tooltip title="Excepciones de pago">
                <IconButton
                  onClick={() => navigate('/excepciones-pago')}
                  sx={{
                    color: '#4b5563',
                    '&:hover svg': { color: '#1f2937' }
                  }}
                >
                  <RuleIcon />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title="Usuarios">
              <IconButton
                onClick={() => navigate('/usuarios')}
                sx={{
                  color: '#4b5563',
                  '&:hover svg': { color: '#1f2937' }
                }}
              >
                <GroupIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Refrescar Facturas VV">
              <IconButton
                onClick={handleRefreshCfdiTable}
                sx={{
                  color: '#4b5563',
                  '&:hover svg': { color: '#1f2937' }
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Cerrar sesión">
              <IconButton
                onClick={handleLogout}
                sx={{
                  color: '#4b5563',
                  '&:hover svg': { color: '#1f2937' }
                }}
              >
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3, mt: '50px !important' }}
      >
        <Routes>
          <Route
            path="/"
            element={<CfdiTable key={cfdiRefreshKey} ref={tableRef} onAddClick={() => setOpenUpload(true)} />}
          />
          {/* Ajusta las rutas según necesites */}
          <Route path="/pagos" element={<PagosProv />} />
          <Route path="/pedidos" element={<PedidosTable />} />
          <Route path="/usuarios" element={<Usuarios />} />
          <Route path="/transportes" element={<CartaPorteTable />} />
          <Route path="/entradas-mercancia" element={<EntradasMercancia />} />
		            <Route
            path="/excepciones-pago"
            element={canManagePaymentExceptions ? <PaymentExceptions /> : <Navigate to="/" />}
          />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>

        <UploadModal
          open={openUpload}
          onClose={() => setOpenUpload(false)}
          refreshTable={handleRefreshCfdiTable}
        />
      </Box>

      {/* Burbuja del agente: siempre disponible */}
      <ChatWidget user={user} />
    </Box>
  );
};

const MainLayout = () => {
  const [user, setUser] = useState(null);

  // Control para mostrar el modal por día (zona MX)
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    axios
      .get(`${API_BASE}/api/validate-session`, { withCredentials: true })
      .then(res => setUser(res.data.user))
      .catch(() => setUser(null));
  }, []);

  // Decide si mostrar el modal: si no está marcado "leído" para HOY (zona MX)
  useEffect(() => {
    if (user) {
      setTimeout(() => {
        try {
          const key = getWelcomeAckKeyDaily(user); // clave incluye fecha MX + versión + usuario
          const acknowledged = localStorage.getItem(key) === '1';
          setShowWelcome(!acknowledged);
        } catch (_e) {
          setShowWelcome(true);
        }
      }, 50);
    } else {
      setShowWelcome(false);
    }
  }, [user]);

  if (!user) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles styles={{ body: { backgroundColor: '#f8fafc' } }} />
        <LoginScreen onLoginSuccess={setUser} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles styles={{ body: { backgroundColor: '#f8fafc' } }} />
      <Router>
        <MainLayoutContent user={user} setUser={setUser} />
      </Router>

      {/* Modal de bienvenida con PDF: lectura diaria, sin poder cerrar hasta "Leído" */
     /* <WelcomePDFModal
        open={showWelcome}
        onAcknowledge={() => setShowWelcome(false)}
        user={user}
      />*/}
    </ThemeProvider>
  );
};

export default MainLayout;
